import { textColorFields } from '../_shared/textColor.js';
import { universalFields, mediaRadiusField } from '../_shared/universal.js';
import { tabColumnBlockSchema } from '../../blocks/tab-column/index.js';
import { tabVideoFields } from '../_shared/tabVideo.js';
/**
 * tabbed-columns — **Multicolumn, behind a tab bar.**
 *
 * The two sections it is made of already existed and neither one answered the ask (fondatrice, 18/09/2026,
 * the HUAWEI WATCH reference): `custom-columns` (Multicolumn) paints one uniform row of media cards and has
 * no tabs; `content-tabs` has the pill bar but each panel is a SINGLE image + paragraph split, so two tabs
 * render as two stacked buttons over one lonely block of text — which is exactly the screenshot that came
 * back. This section is the crossing: a pill bar switches between GROUPS of columns, and each group is a
 * Multicolumn row of its own.
 *
 * ── The block model ───────────────────────────────────────────────────────────────────────────────────
 * A `tab` block is a container: its label is the button, and its CHILDREN are that tab's columns
 * (`tab-column`, the globally-registered card — media + heading + text + link). The rail therefore reads
 * "Tab › its columns", a seller adds a column INSIDE the tab it belongs to, and no ordering convention has
 * to be learnt. Same container pattern as `custom-columns-new`.
 *
 * ── The engine is borrowed, not rewritten ─────────────────────────────────────────────────────────────
 * The markup deliberately emits `content-tabs`' contract — `[data-aqx-content-tabs]` on the widget,
 * `.content-tab-button` on the buttons, `.content-tab` on the panels, `.content-tab-buttom__active-bg` on
 * the sliding pill — so `contentTabsInit` drives it verbatim in BOTH hosts: the storefront boots it from
 * `Layout.astro`'s `initContentTabsAll(document)` with no new wiring at all, and the builder canvas rebinds
 * it on re-render. That engine also carries the deferred-video rule (only the visible tab's clips are ever
 * downloaded), which matters far more here than it did there — a 4-tab × 3-column section is twelve clips.
 *
 * NO-JS FLOOR: SSR marks tab 0 and panel 0 active, so a visitor without JavaScript sees the first tab's
 * columns in full. The tab bar is progressive enhancement, never the thing that makes content appear.
 */
const SCHEME_6 = [
    { value: 'accent-1', label: 'Accent 1' },
    { value: 'accent-2', label: 'Accent 2' },
    { value: 'background-1', label: 'Background 1' },
    { value: 'background-2', label: 'Background 2' },
    { value: 'inverse', label: 'Inverse' },
    { value: 'custom', label: 'Custom' },
];
const HEADING_SIZE = [
    { value: 'h3', label: 'Extra small' },
    { value: 'h2', label: 'Small' },
    { value: 'h1', label: 'Medium' },
    { value: 'h0', label: 'Large' },
];
const ALIGN_3 = [
    { value: 'left', label: 'Left' },
    { value: 'center', label: 'Center' },
    { value: 'right', label: 'Right' },
];
export const tabbedColumnsSchema = {
    type: 'tabbed-columns',
    name: 'Tabbed columns',
    version: 1,
    templates: ['index', 'collection', 'page', 'product', 'thank-you'],
    fields: [
        // ── General ───────────────────────────────────────────────────────────────
        {
            // Le titre de la section est du CONTENU, pas un libellé d'interface : sans `aiWritable`, le
            // dictionnaire (0050) l'exclut du relevé et la page composée titre la section du nom du composant.
            id: 'heading',
            type: 'text',
            label: 'Heading',
            group: 'General',
            default: 'Why choose us',
            defaultKey: 'section.why_choose_us',
            aiWritable: true,
            maxLength: 120,
        },
        {
            id: 'headingSize',
            type: 'select',
            label: 'Heading size',
            group: 'General',
            default: 'h1',
            options: HEADING_SIZE,
        },
        {
            id: 'headingAlignment',
            type: 'select',
            label: 'Heading alignment',
            group: 'General',
            default: 'center',
            options: ALIGN_3,
        },
        // ── Tabs (the pill bar) ───────────────────────────────────────────────────
        {
            id: 'tabsAlignment',
            type: 'select',
            label: 'Tab bar alignment',
            group: 'Tabs',
            default: 'center',
            options: ALIGN_3,
        },
        {
            // The HUAWEI reference puts the bar UNDER the media it switches. Both readings are legitimate — a
            // bar on top announces a choice, a bar below reads as a caption control — so it is a setting rather
            // than a layout we picked for the seller.
            id: 'tabsPosition',
            type: 'select',
            label: 'Tab bar position',
            group: 'Tabs',
            default: 'above',
            options: [
                { value: 'above', label: 'Above the columns' },
                { value: 'below', label: 'Below the columns' },
            ],
        },
        {
            id: 'buttonsColorScheme',
            type: 'select',
            label: 'Tab buttons color',
            group: 'Tabs',
            default: 'accent-1',
            options: SCHEME_6,
        },
        {
            id: 'customTabsColor',
            type: 'color',
            label: 'Custom tab buttons & border color',
            group: 'Tabs',
            default: { ref: 'custom', value: '#000000' },
            visibleWhen: { field: 'buttonsColorScheme', equals: 'custom' },
        },
        {
            id: 'customTabsColorLabel',
            type: 'color',
            label: 'Custom active button label color',
            group: 'Tabs',
            default: { ref: 'custom', value: '#FFFFFF' },
            visibleWhen: { field: 'buttonsColorScheme', equals: 'custom' },
        },
        {
            id: 'buttonBorderRadius',
            type: 'range',
            label: 'Buttons corner radius',
            group: 'Tabs',
            min: 0,
            max: 40,
            step: 2,
            unit: 'px',
            default: 30,
        },
        {
            id: 'buttonsStyle',
            type: 'select',
            label: 'Buttons container style',
            group: 'Tabs',
            default: 'outlined',
            options: [
                { value: 'outlined', label: 'Outlined' },
                { value: 'shadow', label: 'Drop shadow' },
            ],
        },
        {
            id: 'activeButtonAnimation',
            type: 'select',
            label: 'Active button animation',
            group: 'Tabs',
            default: 'moving',
            info: 'Choose "Moving" only if all your buttons fit on one row.',
            options: [
                { value: 'opacity', label: 'Opacity' },
                { value: 'moving', label: 'Moving' },
            ],
        },
        // ── Columns ───────────────────────────────────────────────────────────────
        {
            id: 'imageRatio',
            type: 'select',
            label: 'Image ratio',
            group: 'Columns',
            default: 'adapt',
            options: [
                { value: 'adapt', label: 'Adapt to image' },
                { value: 'portrait', label: 'Portrait' },
                { value: 'square', label: 'Square' },
                { value: 'circle', label: 'Circle' },
            ],
        },
        {
            id: 'columnAlignment',
            type: 'select',
            label: 'Text alignment',
            group: 'Columns',
            default: 'center',
            options: ALIGN_3,
        },
        {
            id: 'cardsCornerRadius',
            type: 'range',
            label: 'Cards corner radius',
            group: 'Columns',
            min: 0,
            max: 40,
            step: 2,
            unit: 'px',
            default: 0,
        },
        {
            id: 'stretchCards',
            type: 'checkbox',
            label: 'Make all cards same height',
            group: 'Columns',
            default: true,
        },
        // ── Desktop ───────────────────────────────────────────────────────────────
        {
            id: 'columnsDesktop',
            type: 'range',
            label: 'Columns per row',
            group: 'Desktop',
            min: 1,
            max: 4,
            step: 1,
            default: 3,
        },
        {
            id: 'desktopSpacing',
            type: 'range',
            label: 'Spacing',
            group: 'Desktop',
            min: 0,
            max: 60,
            step: 4,
            unit: 'px',
            default: 24,
        },
        {
            id: 'buttonsDesktopFontSize',
            type: 'range',
            label: 'Buttons font size',
            group: 'Desktop',
            min: 14,
            max: 30,
            step: 1,
            unit: 'px',
            default: 15,
        },
        {
            id: 'desktopFullSizeButtons',
            type: 'checkbox',
            label: 'Expand buttons to full width',
            group: 'Desktop',
            default: false,
        },
        // ── Mobile ────────────────────────────────────────────────────────────────
        {
            id: 'columnsMobile',
            type: 'select',
            label: 'Columns per row',
            group: 'Mobile',
            default: '1',
            options: [
                { value: '1', label: '1 column' },
                { value: '2', label: '2 columns' },
            ],
        },
        {
            id: 'buttonsMobileFontSize',
            type: 'range',
            label: 'Buttons font size',
            group: 'Mobile',
            min: 8,
            max: 25,
            step: 1,
            unit: 'px',
            default: 13,
        },
        {
            id: 'mobileFullSizeButtons',
            type: 'checkbox',
            label: 'Expand buttons to full width',
            group: 'Mobile',
            default: true,
        },
        // ── Media (how the cards' clips play) ────────────────────────────────────
        ...tabVideoFields,
        // ── Universal band (color scheme / width / padding / animation) ───────────
        mediaRadiusField,
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        ...universalFields,
    ],
    // Only a TAB may sit directly in the section — a column always belongs to one. `blocks` below still has
    // to declare BOTH schemas: it is the list the preset instantiates from, the rail reads its `accepts`
    // matrix from, and the inspector renders fields from. Declaring a type there does NOT make it addable at
    // section level; `accepts` decides that (same split as main-product's gallery/details tree).
    accepts: ['tab'],
    blocks: [
        {
            type: 'tab',
            name: 'Tab',
            max: 8,
            accepts: ['tab-column'],
            maxBlocks: 12,
            fields: [
                {
                    // Le libellé d'un onglet est du CONTENU («Composition», «Entretien»), pas un bouton d'achat :
                    // `aiWritable` le rend visible au composeur, et le libellé du champ dit « Tab label » et non
                    // « Tab BUTTON label », que PageAssembler.deadButtons efface hors fiche d'achat.
                    id: 'btnLabel',
                    type: 'text',
                    label: 'Tab label',
                    default: 'Tab button',
                    defaultKey: 'section.tab_button',
                    aiWritable: true,
                    maxLength: 60,
                },
                {
                    id: 'btnIcon',
                    type: 'icon-name',
                    label: 'Icon',
                    info: 'A Google Material Symbols name (e.g. check_circle). Optional.',
                },
                { id: 'filledBtnIcon', type: 'checkbox', label: 'Filled icon', default: false },
                { id: 'btnCustomIcon', type: 'image', label: 'Custom icon (overrides the icon)' },
            ],
        },
        tabColumnBlockSchema,
    ],
    maxBlocks: 8,
};
/** One tab + its three placeholder columns. Text comes from the shared catalogue, never a hard-coded FR string. */
const tab = (label) => ({
    type: 'tab',
    settings: { btnLabel: label, btnIcon: '' },
    blocks: [
        { type: 'tab-column', settings: { title: '@t:section.column_title' } },
        { type: 'tab-column', settings: { title: '@t:section.column_title' } },
        { type: 'tab-column', settings: { title: '@t:section.column_title' } },
    ],
});
export const tabbedColumnsPresets = [
    {
        name: 'Tabbed columns',
        settings: {
            heading: '@t:section.why_choose_us',
            headingSize: 'h1',
            headingAlignment: 'center',
            tabsAlignment: 'center',
            tabsPosition: 'above',
            buttonsColorScheme: 'accent-1',
            buttonBorderRadius: 30,
            buttonsStyle: 'outlined',
            activeButtonAnimation: 'moving',
            imageRatio: 'adapt',
            columnAlignment: 'center',
            cardsCornerRadius: 0,
            stretchCards: true,
            columnsDesktop: 3,
            desktopSpacing: 24,
            buttonsDesktopFontSize: 15,
            desktopFullSizeButtons: false,
            columnsMobile: '1',
            buttonsMobileFontSize: 13,
            mobileFullSizeButtons: true,
            colorScheme: 'light',
            paddingTop: 48,
            paddingBottom: 48,
        },
        // TWO tabs — the ask was literally "deux boutons". Each opens its own row of three editable columns,
        // so the section is obviously a tabbed multicolumn the moment it lands on the page.
        blocks: [tab('@t:section.tab1_label'), tab('@t:section.tab2_label')],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for every section to paint the picker while rendering
 * only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it without
 * dragging every section's render code into the editor's entry chunk.
 */
export const tabbedColumnsMeta = {
    label: 'Tabbed columns',
    group: 'Content',
    icon: 'view-column',
};
//# sourceMappingURL=schema.js.map